import { Box, Center, Spinner, Tag, Text, Wrap } from "@chakra-ui/react";
import useInterpolateTranslatedField from "@raiden/library/hooks/useInterpolateTranslatedField";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { FormattedMessage, useIntl } from "react-intl";
import Breadcrumbs from "../../../components/Breadcrumbs";
import CenteredContent from "../../../components/CenteredContent";
import Splash from "../../../components/Splash";
import SWRHandler from "../../../components/SWR/SWRHandler";
import PostsView from "../../Posts/View";

/**
 * @typedef {Object} Props
 * @property {import("swr").SWRResponse<import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post>>} swrResponsePost
 *
 * @param {Props} props
 */
function ArticlesView({ swrResponsePost }) {
  const intl = useIntl();

  const translate = useInterpolateTranslatedField();

  const post = swrResponsePost.data?.data;

  return (
    <Box mt="3.75rem" mb="2rem">
      <Box px="1rem">
        <CenteredContent maxWidth="1280px">
          <Breadcrumbs
            renderOnMobile={true}
            items={[
              {
                title: intl.formatMessage({ defaultMessage: "Accueil" }),
                url: generateFrontPath({ id: "@home" }),
              },
              {
                title: intl.formatMessage({ defaultMessage: "L'actualité" }),
                url: generateFrontPath({ id: "@articles.search" }),
              },
              ...(post?.categories?.[0]?.name
                ? [
                    {
                      title: translate(post.categories?.[0].name) ?? "",
                      url: generateFrontPath({
                        id: "@articles.search",
                        query: {
                          category_id: [post.categories?.[0].id],
                        },
                      }),
                    },
                  ]
                : []),
              ...(post?.current_revision?.title
                ? [
                    {
                      title: translate(post?.current_revision?.title) ?? "",
                    },
                  ]
                : []),
            ]}
          />
        </CenteredContent>
      </Box>

      <Box mt="1rem">
        <SWRHandler
          swrResponse={swrResponsePost}
          keepDataOnRevalidation={true}
          loading={
            <Center>
              <Spinner size="xl" color="brandPrimary.600" />
            </Center>
          }
          noResult={
            <Splash
              title={intl.formatMessage({
                defaultMessage: "Article non trouvé",
              })}
              description={intl.formatMessage({
                defaultMessage:
                  "L'article que vous recherchez n'existe pas ou n'est plus disponible.",
              })}
              src={generateFrontPath({
                id: "internal-assets",
                parameters: { filePath: "illustrations/empty-public.svg" },
                includeBasePath: true,
              })}
            />
          }>
          {({ data: post }) => (
            <>
              <Box px="1rem">
                <CenteredContent maxWidth="1280px">
                  <Text fontSize="3rem" fontWeight={400} lineHeight="normal">
                    {translate(post?.current_revision?.title)}
                  </Text>

                  {post?.current_revision?.subtitle && (
                    <Text
                      fontSize="1.5rem"
                      fontWeight={400}
                      lineHeight="120%"
                      color="gray.500"
                      mt="1.5rem">
                      {translate(post.current_revision.subtitle)}
                    </Text>
                  )}

                  <Wrap spacing="1rem" mt="1rem">
                    {post.categories?.map((category) => (
                      <Tag
                        variant="solid"
                        size="lg"
                        colorScheme="brandPrimary"
                        borderRadius="full"
                        key={category.id}>
                        {translate(category.name)}
                      </Tag>
                    ))}

                    {post.publication_date !== null && (
                      <Text
                        fontSize="1.125rem"
                        fontWeight={400}
                        lineHeight="1.75rem">
                        <FormattedMessage
                          defaultMessage="Publié le {date}"
                          values={{
                            date: intl.formatDate(post.publication_date, {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }),
                          }}
                        />
                      </Text>
                    )}
                  </Wrap>
                </CenteredContent>
              </Box>

              <Box mt="2rem">
                <CenteredContent maxWidth="calc(1280px + 32px)">
                  <PostsView post={post} />
                </CenteredContent>
              </Box>
            </>
          )}
        </SWRHandler>
      </Box>
    </Box>
  );
}

export default ArticlesView;
